import {
  GetCurrentPatientQueryVariables,
  useGetCurrentPatientLazyQuery as useGetCurrentPatientLazyQueryBase,
} from '../../generated/graphql';

import { Auth0ContextInterface, useAuth0 as useAuth0Base } from '@auth0/auth0-react';
import { useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';

import { authedAtTimestampAtom } from '../../auth/state';
import { appointmentInfoAtom, useAppDependenciesContext } from '../../state';
import {
  telehealthLogOutUnauthorizedClientsGate,
  telehealthRequiredConsentsWaitingRoomBlockGate,
} from '../../statsig/gates';
import {
  UseLazyClientRequiresAuthedIntroRoomDependencies,
  createClientRequiresAuthedIntroRoomVariables,
  useLazyClientRequiresAuthedIntroRoom,
} from '../introduction-room/auth/useClientRequiresAuthedIntroRoom';
import { useEnterWaitingRoomCallback as useEnterWaitingRoomCallbackBase } from '../nav/useEnterWaitingRoomCallback';

export const UNVERIFIED = 'UNVERIFIED';
type ConsentVerificationStatus = typeof UNVERIFIED | boolean;
export const ADMIT_UNVERIFIED_LOG_MESSAGE =
  'Missing data for authenticated client, admitting unverified client to waiting room';

type Dependencies = {
  useAuth0: () => Pick<Auth0ContextInterface, 'logout'>;
  useEnterWaitingRoomCallback: typeof useEnterWaitingRoomCallbackBase;
  useGetCurrentPatientLazyQuery: typeof useGetCurrentPatientLazyQueryBase;
  useLazyClientRequiresAuthedIntroRoomDependencies?: UseLazyClientRequiresAuthedIntroRoomDependencies;
};

/**
 * Returns a callback that gracefully authorizes the current user, checks if the
 * client has all of the required legal agreements signed, and automatically
 * enters the waiting room if successful.
 *
 * @param dependencies - Optional dependency overrides (e.g. for testing)
 */
export function useGracefullyAuthorizeCurrentUserCallback(
  {
    useAuth0,
    useEnterWaitingRoomCallback,
    useGetCurrentPatientLazyQuery,
    useLazyClientRequiresAuthedIntroRoomDependencies,
  }: Dependencies = {
    useAuth0: useAuth0Base,
    useEnterWaitingRoomCallback: useEnterWaitingRoomCallbackBase,
    useGetCurrentPatientLazyQuery: useGetCurrentPatientLazyQueryBase,
  },
) {
  const { logout } = useAuth0();
  const [getCurrentPatientBase] = useGetCurrentPatientLazyQuery();
  const getClientRequiresAuthedIntroRoom = useLazyClientRequiresAuthedIntroRoom(
    useLazyClientRequiresAuthedIntroRoomDependencies,
  );
  const setAuthedAtTimestamp = useSetAtom(authedAtTimestampAtom);
  const enterWaitingRoom = useEnterWaitingRoomCallback();
  const {
    logger,
    Statsig: { checkGate },
  } = useAppDependenciesContext();
  const getAppointmentInfo = useAtomCallback(useCallback((get) => get(appointmentInfoAtom), []));

  return useCallback(
    async ({
      variables,
      getCurrentPatient = getCurrentPatientBase,
    }: {
      variables: GetCurrentPatientQueryVariables;
      getCurrentPatient?: typeof getCurrentPatientBase;
    }) => {
      const { patientShortId } = variables;
      const isRequiredConsentsWaitingRoomBlockEnabled = checkGate(telehealthRequiredConsentsWaitingRoomBlockGate);

      const handleSuccess = (preferredName: string, requiresAuthedIntroRoom: ConsentVerificationStatus) => {
        setAuthedAtTimestamp(Date.now());
        if (requiresAuthedIntroRoom === true) return;
        if (requiresAuthedIntroRoom === UNVERIFIED && isRequiredConsentsWaitingRoomBlockEnabled) {
          logger.warn(ADMIT_UNVERIFIED_LOG_MESSAGE, { patientShortId });
        }
        enterWaitingRoom(preferredName);
      };

      let requiresAuthedIntroRoom: ConsentVerificationStatus = UNVERIFIED;
      try {
        const appointmentShortId = getAppointmentInfo()?.shortId;
        const variables = createClientRequiresAuthedIntroRoomVariables(patientShortId, appointmentShortId);
        if (!variables) {
          logger.warn('Missing patientShortId for client requires authed intro room query', {
            patientShortId,
          });
        } else {
          const result = await getClientRequiresAuthedIntroRoom({ variables });
          if (result?.data?.clientRequiresAuthedIntroRoom) {
            requiresAuthedIntroRoom = result.data.clientRequiresAuthedIntroRoom.requireAuth;
          }
        }
      } catch (err) {
        const error = err as Error;
        // Failure to fetch this data should not block admission to the waiting room
        logger.warn('Failed to fetch client requires authed intro room', { patientShortId }, error);
      }

      try {
        const { data, error } = await getCurrentPatient({ variables });

        // Not using the `onCompleted` callback because it executes
        // asynchronously which can result in the intro room briefly flashing
        // before the waiting room
        const preferredName = data?.patientInfo?.preferredName;

        if (!preferredName) {
          logger.warn(
            "Failed to get patient's preferred name. This likely indicates the client's auth session has expired or the client is authenticated with an account that does not match this patient. This user needs to re-authenticate and will be logged out.",
            { patientShortId },
            error,
          );
          if (checkGate(telehealthLogOutUnauthorizedClientsGate)) {
            logout({ openUrl: false });
          }
          return;
        }
        return handleSuccess(preferredName, requiresAuthedIntroRoom);
      } catch (err) {
        const error = err as Error;
        logger.error('Failed to fetch patient', { patientShortId }, error);
        if (checkGate(telehealthLogOutUnauthorizedClientsGate)) {
          logout({ openUrl: false });
        }
        return;
      }
    },
    [
      checkGate,
      enterWaitingRoom,
      getAppointmentInfo,
      getClientRequiresAuthedIntroRoom,
      getCurrentPatientBase,
      logger,
      logout,
      setAuthedAtTimestamp,
    ],
  );
}
